import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, createEventDynamicLink } from '../firebase/firebase';
import Navbar from '../components/Navbar';

const EventDetail = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dynamicLink, setDynamicLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);

        // Query events collection by slug
        const eventsRef = collection(db, 'events');
        const q = query(eventsRef, where('slug', '==', slug));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Get the first document that matches the slug
          const eventDoc = querySnapshot.docs[0];
          setEvent({ id: eventDoc.id, ...eventDoc.data() });

          // Generate dynamic link
          const link = await createEventDynamicLink(slug);
          setDynamicLink(link);
        } else {
          // If no event found in Firestore, check dummy events
          const dummyEvent = dummyEvents.find(e => e.slug === slug);
          if (dummyEvent) {
            setEvent(dummyEvent);
            setDynamicLink(`https://lucien14.page.link/${slug}`);
          } else {
            setError('Event not found');
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event details. Please try again later.');
        setLoading(false);
      }
    };

    fetchEvent();
  }, [slug]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(dynamicLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
      });
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg">
        <Navbar />
        <div className="container-custom py-20 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-muted-gold"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-light-bg">
        <Navbar />
        <div className="container-custom py-20 text-center">
          <h2 className="text-2xl font-heading text-red-400 mb-4">{error}</h2>
          <Link to="/#events" className="btn btn-primary">Back to Events</Link>
        </div>
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-light-bg">
      <Navbar />

      <motion.div
        className="container-custom py-12 md:py-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/#events" className="inline-flex items-center text-muted-gold mb-8 hover:underline">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Events
        </Link>

        <div className="bg-light-bg-alt border border-sandy-beige/30 rounded-lg overflow-hidden shadow-xl">
          {/* Event Banner */}
          <div className="relative h-64 md:h-96 overflow-hidden">
            {event.image ? (
              <img
                src={event.image}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-light-bg flex items-center justify-center">
                <span className="text-muted-gold text-2xl font-heading">{event.title}</span>
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-light-bg-alt to-transparent"></div>
          </div>

          {/* Event Details */}
          <div className="p-6 md:p-10">
            <motion.h1
              className="text-3xl md:text-5xl font-heading font-bold text-text-dark mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {event.title}
            </motion.h1>

            <motion.div
              className="flex items-center mb-6 text-muted-gold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{formatDate(event.date)}</span>
            </motion.div>

            <motion.div
              className="prose prose-lg max-w-none mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <p className="text-text-dark whitespace-pre-line">{event.description}</p>
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <button
                className="btn btn-primary"
                onClick={() => alert('RSVP functionality would go here')}
              >
                RSVP to Event
              </button>

              <button
                className="btn btn-secondary flex items-center justify-center"
                onClick={handleCopyLink}
              >
                {linkCopied ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Link Copied!
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share Event
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Dummy events data for development
const dummyEvents = [
  {
    id: '1',
    title: 'Vinyl Night: Jazz Classics',
    description: 'An evening of classic jazz vinyl records, paired with signature cocktails and small plates.\n\nJoin us for a journey through the golden era of jazz, featuring records from Miles Davis, John Coltrane, Thelonious Monk, and more. Our resident mixologist will create custom cocktails inspired by each artist, while the kitchen serves up small plates designed to complement the listening experience.\n\nSpace is limited to ensure an intimate atmosphere.',
    date: '2023-06-15T19:00:00',
    image: '/images/events/vinyl-night.jpg',
    slug: 'vinyl-night-jazz-classics',
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: '2',
    title: 'Tasting Menu Experience',
    description: 'A special 7-course tasting menu featuring seasonal ingredients and experimental techniques.\n\nThis exclusive dining experience showcases the best of the season through a carefully crafted progression of dishes. Each course tells a story and builds upon the last, creating a complete narrative through taste and texture.\n\nWine pairings are available as an optional addition. Dietary restrictions can be accommodated with advance notice.',
    date: '2023-07-22T18:30:00',
    image: '/images/events/tasting-menu.jpg',
    slug: 'tasting-menu-experience',
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: '3',
    title: 'Whiskey & Chocolate Pairing',
    description: 'Explore the complex flavors of single malt whiskeys paired with artisanal chocolates.\n\nThis guided tasting experience will take you through five premium single malt whiskeys, each paired with a custom-made chocolate creation. Learn about the production processes, flavor profiles, and the art of pairing as you indulge in these complementary luxury items.\n\nAll participants receive a detailed tasting guide to take home.',
    date: '2023-08-10T20:00:00',
    image: '/images/events/whiskey-pairing.jpg',
    slug: 'whiskey-chocolate-pairing',
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: '4',
    title: 'Late Night Session',
    description: 'After-hours gathering with ambient music, craft cocktails, and midnight snacks.\n\nWhen the regular world winds down, our late night sessions begin. Join us for an evening of atmospheric electronic music, expertly crafted cocktails, and a special menu of elevated late-night comfort food.\n\nThe perfect way to end your evening or start your night, our Late Night Session runs until 2am.',
    date: '2023-09-05T22:00:00',
    image: '/images/events/late-night.jpg',
    slug: 'late-night-session',
    createdAt: new Date(),
    updatedAt: new Date()
  }
];

export default EventDetail;
